/**
 * Submit latch for a form that posts natively, so a slow endpoint cannot
 * collect a second copy of the same submission from an impatient second press.
 *
 * Spread into an Alpine component whose x-data sits on the <form>, and call
 * initSubmitGuard() from that component's init(). Bind one entry point:
 *
 *   @submit="onSubmit($event)"       covers the Enter key as well as the
 *                                    button, and runs only after the browser's
 *                                    own constraint validation
 *   @click="onSubmitClick($event)"   for a form that cannot declare a submit
 *                                    directive
 *
 * Captcha::printCaptcha() skips binding its reCAPTCHA v3 token-refresh submit
 * handler on any form that declares @submit / x-on:submit, so a form carrying a
 * captcha must bind onSubmitClick instead.
 *
 * The submit button never receives the disabled attribute. Setting disabled
 * inside the press that triggers the submit cancels that submit in every major
 * browser. The first press latches and lets the native submit proceed, and
 * every later press is cancelled here.
 *
 * A component that must run pre-submit work, or block the submit outright,
 * declares prepareSubmit(event) and returns false to leave the button live.
 * Blocking the submit itself belongs to that method.
 */
export function submitGuard({ recoveryMs = 15000 } = {}) {
    return {
        submitting: false,
        submitRecoveryTimer: null,

        initSubmitGuard() {
            // The back-forward cache restores live JS state, so navigating back
            // to a submitted form returns a button still latched into its
            // spinner state and refusing every press.
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    this.stopSubmitting();
                }
            });
        },

        onSubmit(event) {
            if (this.submitting) {
                event.preventDefault();

                return;
            }

            if (!this.runPrepareSubmit(event)) {
                return;
            }

            this.latchSubmit();
        },

        onSubmitClick(event) {
            if (this.submitting) {
                event.preventDefault();

                return;
            }

            if (!this.runPrepareSubmit(event)) {
                return;
            }

            // Latch only when the form will actually submit. checkValidity() is
            // false while a required field is empty, and a press blocked by
            // native validation performs no navigation, so latching there would
            // wedge the button and lock the user out of the form.
            const form = this.$root;
            if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) {
                return;
            }

            this.latchSubmit();
        },

        runPrepareSubmit(event) {
            return typeof this.prepareSubmit !== 'function' || this.prepareSubmit(event) !== false;
        },

        latchSubmit() {
            this.submitting = true;

            // Safety net for a submit that never navigates: a hung captcha, a
            // blocked script. The latch would otherwise hold until a manual
            // refresh, which costs the user more than the duplicate this guard
            // prevents. A real submission, including a slow multipart upload,
            // navigates well inside this window.
            this.stopSubmitRecoveryTimer();
            this.submitRecoveryTimer = window.setTimeout(() => {
                this.submitting = false;
                this.submitRecoveryTimer = null;
            }, recoveryMs);
        },

        stopSubmitRecoveryTimer() {
            if (this.submitRecoveryTimer !== null) {
                window.clearTimeout(this.submitRecoveryTimer);
                this.submitRecoveryTimer = null;
            }
        },

        stopSubmitting() {
            this.submitting = false;
            this.stopSubmitRecoveryTimer();
        },
    };
}
